import React, { useState, useEffect, memo, useCallback } from "react";
import { useSportsData } from "../App";
import { useNavigate, useLocation, Link } from "react-router-dom";
import LoginModal from "./LoginModal";
import logoImage from "./logo.png";

const Navbar = memo(({ loginButtonText }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { data: sports = [] } = useSportsData();

  const navigate = useNavigate();
  const location = useLocation();

  // Memoized menu items
  const menuItems = React.useMemo(() => [
    { label: "Land", type: "land" },
    { label: "Air", type: "air" },
    { label: "Water", type: "water" },
    { label: "Institute", link: "#" },
    { label: "Know Your Sport", link: "#" },
    { label: "Blog", link: "/blogs-page" },
    { label: "Gear", link: "#" },
    { label: "Community", link: "#" },
    { label: "Become a Partner", link: "#" },
  ], []);

  // Check login status on mount
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const name = localStorage.getItem("user_name");
    if (token && name) {
      setIsLoggedIn(true);
      setUserName(name);
    }
  }, []);

  // Handle window resize with memoized handler
  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // Handle scroll events (passive & avoids redundant state updates)
  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 50;
    setIsScrolled(prev => (prev !== scrolled ? scrolled : prev));
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Memoized event handlers
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const handleFilterClick = useCallback((type) => {
    if (!sports || sports.length === 0) {
      console.error("Sports data is not available.");
      return;
    }

    const filteredSports = sports.filter((sport) => sport.type?.toLowerCase() === type?.toLowerCase());
    const route = `/${type}-adventure`;
    navigate(route, { state: { filteredSports } });
  }, [sports, navigate]);

  const handleLoginClick = useCallback(() => {
    setIsLoginModalOpen(true);
  }, []);

  const handleLoginSuccess = useCallback((token, name) => {
    localStorage.setItem("access_token", token);
    localStorage.setItem("user_name", name);
    setIsLoggedIn(true);
    setUserName(name);
    setIsLoginModalOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_name");
    setIsLoggedIn(false);
    setUserName("");
    setIsDropdownOpen(false);
    alert("Logged out successfully!");
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, closeDropdown]);

  const isHomePage = location.pathname === "/";




  const NavButton = ({ item, isMobile = false, onClick }) => {
    const baseClasses =
      "text-white hover:text-gray-300 transition-colors duration-200 whitespace-nowrap text-sm lg:text-base";

    const handleClick = () => {
      if (item.link === "#") return; // Prevent click on disabled items

      if (item.type) {
        handleFilterClick(item.type);
      } else if (item.link) {
        navigate(item.link);
      }
      if (onClick) onClick(); // Close mobile menu if needed
    };

    if (item.link === "#") {
      return (
        <span
          className={`${baseClasses} opacity-50 cursor-not-allowed`}
          title="Coming Soon"
        >
          {item.label}
        </span>
      );
    }

    return (
      <button onClick={handleClick} className={baseClasses}>
        {item.label}
      </button>
    );
  };

  return (
    <header
      className={`fixed w-full z-50 transition-colors duration-300 ${isScrolled || !isHomePage || windowWidth <= 768
        ? "bg-primary"
        : "bg-transparent"
        }`}
    >
      <nav className="w-full px-6 py-3" role="navigation" aria-label="Main navigation">
        <div className="flex justify-between items-center w-full">
          {/* Logo */}
          <div className="flex-shrink-0 text-white text-2xl font-bold">
            <Link
              to="/"
              className="flex items-center cursor-pointer"
              aria-label="Adventure Scanner Home"
            >
              <img
                src={logoImage}
                alt="Adventure Scanner Logo"
                className="h-10"
                loading="lazy"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8 flex-grow justify-center">
            {menuItems.map((item, index) => (
              <NavButton key={index} item={item} />
            ))}
          </div>

          {/* User Profile Section - Desktop */}
          <div className="hidden md:block">
            {isLoggedIn ? (
              <div className="flex items-center space-x-4 relative dropdown-container">
                <button
                  className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={toggleDropdown}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center text-black font-bold">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm lg:text-base text-white whitespace-nowrap truncate ">Hi, {userName}</span>
                  <svg
                    className={`w-4 h-4 text-white transition-transform ${isDropdownOpen ? "transform rotate-180" : ""
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-12 right-0 bg-white rounded-lg shadow-lg w-48 py-2">
                    <nav role="menu" aria-label="User menu">
                      <Link
                        to="/profile"
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                        role="menuitem"
                        onClick={closeDropdown}
                      >
                        User Profile
                      </Link>
                      <Link
                        to="/bookings"
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                        role="menuitem"
                        onClick={closeDropdown}
                      >
                        My Bookings
                      </Link>
                      <Link
                        to="/wishlist"
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                        role="menuitem"
                        onClick={closeDropdown}
                      >
                        Wishlist
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                        role="menuitem"
                      >
                        Logout
                      </button>
                    </nav>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLoginClick}
                className="bg-white text-black px-6 py-2 rounded-full hover:bg-gray-200 transition-colors duration-300"
                aria-label="Login"
              >
                {loginButtonText}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded p-1"
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label="Toggle mobile menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className={`${isMobileMenuOpen ? "block" : "hidden"
            } md:hidden bg-neutral-900 mt-4 rounded-lg p-4`}
          role="menu"
          aria-label="Mobile navigation menu"
        >
          <div className="flex flex-col space-y-4">
            {menuItems.map((item, index) => (
              <div key={index} role="menuitem">
                <NavButton item={item} isMobile={true} onClick={() => setIsMobileMenuOpen(false)} />
              </div>
            ))}

            {/* Mobile Auth Section */}
            <div className="border-t border-gray-600 pt-4 mt-4">
              {isLoggedIn ? (
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center space-x-2 text-white mb-2">
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-black font-bold text-sm">
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    <span>Hi, {userName}</span>
                  </div>
                  <Link
                    to="/profile"
                    className="bg-white text-black px-6 py-2 rounded-full text-center hover:bg-gray-200 transition-colors duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    User Profile
                  </Link>
                  <Link
                    to="/bookings"
                    className="bg-gray-200 text-black px-6 py-2 rounded-full text-center hover:bg-gray-300 transition-colors duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Bookings
                  </Link>
                  <Link
                    to="/wishlist"
                    className="bg-gray-200 text-black px-6 py-2 rounded-full text-center hover:bg-gray-300 transition-colors duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Wishlist
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="bg-red-500 text-white px-6 py-2 rounded-full text-center hover:bg-red-600 transition-colors duration-300"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLoginClick}
                  className="bg-white text-black px-6 py-2 rounded-full text-center hover:bg-gray-200 transition-colors duration-300 w-full"
                >
                  {loginButtonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </header>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;